import { useEffect, useState, useCallback } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtMoney, fmtDate, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, StatusBadge } from "@/pages/AppLayout";
import { useCatalog, goCheckout, ProvidersStatus } from "@/pages/Credits";
import { Input } from "@/components/ui/input";

export const SUB_STATUS = { activo: "Activa", cancela_al_final_del_periodo: "Se cancelará al final del periodo", cancelado: "Cancelada", pago_pendiente: "Pago pendiente" };

export default function Plan() {
  const { user, refreshUser } = useAuth();
  const cat = useCatalog();
  const [busy, setBusy] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [sum, setSum] = useState(null);
  const loadSum = useCallback(() => api.get("/billing/summary").then((r) => setSum(r.data)), []);
  useEffect(() => { loadSum(); }, [loadSum]);
  const sub = sum?.subscription;

  const toggle = async (cancel) => {
    try { await api.post(cancel ? "/billing/cancel-subscription" : "/billing/reactivate-subscription"); toast.success(cancel ? "Tu suscripción se cancelará al final del periodo" : "Suscripción reactivada"); refreshUser(); loadSum(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="plan-page">
      <PageHeader eyebrow="Suscripción" title="Plan contratado" subtitle="Los planes recargan créditos cada mes. Precios provisionales en dólares (USD)." action={cat && <ProvidersStatus cat={cat} />} />
      <Card className="mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Plan actual</p>
          <p className="font-display text-3xl font-bold text-white mt-1" data-testid="current-plan">{sum?.plan ? sum.plan.name : "Sin plan"}</p>
          {sub && <div className="mt-2 flex flex-wrap items-center gap-3"><StatusBadge ok={sub.status === "activo"} testId="subscription-status">{SUB_STATUS[sub.status] || sub.status}</StatusBadge><span className="text-xs text-slate-400" data-testid="renewal-date">{sub.cancel_at_period_end ? "Termina" : "Se renueva"}: {fmtDate(sub.current_period_end)}</span></div>}
          {sum && <p className="text-xs text-slate-500 mt-2">Proyectos: {sum.projects}{sum.project_limit != null ? ` de ${sum.project_limit}` : ""}</p>}
        </div>
        {sub && (sub.cancel_at_period_end
          ? <button onClick={() => toggle(false)} data-testid="reactivate-subscription-btn" className="px-4 py-2 rounded-lg border border-emerald-500/30 text-emerald-300 text-sm">Reactivar suscripción</button>
          : <button onClick={() => toggle(true)} data-testid="cancel-subscription-btn" className="px-4 py-2 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10 text-sm">Cancelar suscripción</button>)}
      </Card>
      <div className="flex items-center gap-3 mb-5">
        <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Cupón de descuento (opcional)" className="max-w-xs bg-[#0A0D14] font-mono" data-testid="plan-coupon-input" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {cat?.plans.map((p, i) => {
          const current = sum?.plan?.id === p.id;
          return (
            <Card key={p.id} className={current ? "border-blue-500/60" : i === 1 ? "border-cyan-500/30" : ""} data-testid={`plan-${p.id}`}>
              <h3 className="text-xl font-medium text-white">{p.name}</h3>
              <p className="mt-3 font-display text-4xl font-bold text-white">{fmtMoney(p.price, cat.currency)}<span className="text-sm text-slate-500 font-normal">/mes</span></p>
              <ul className="mt-6 space-y-2 text-sm text-slate-300">
                {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />{f}</li>)}
              </ul>
              <button onClick={() => goCheckout(p.id, setBusy, "stripe", coupon)} disabled={current || !!sub || !!busy || !cat.providers.stripe.configured || !cat.payments_enabled} data-testid={`subscribe-${p.id}`}
                className="mt-8 w-full h-11 rounded-xl bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                {busy === `${p.id}:stripe` && <Loader2 className="w-4 h-4 animate-spin" />}{current ? "Tu plan actual" : sub ? "Cancela tu plan actual para cambiar" : "Suscribirme"}
              </button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
